import { useCallback, useEffect, useMemo, useRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { BowlMutationProvider, useBowlMutation, useFencedState as useState } from '../../lib/bowlMutationContext.jsx'
import { api, request } from '../../lib/api.js'
import { useAuth } from '../../lib/auth.jsx'
import { pricingDeepLink } from '../../lib/championToClose.js'
import {
  FILTERS, applyFilter, secondLabel, secondSource, daysLabel, roleLabel, recordHref, committeeComposerHref, tierCounts,
  ownerChoices,
} from '../../lib/bowlCommittees.js'
import { useAccess } from '../../lib/access.jsx'
import { readinessOf } from '../../lib/assignmentSetup.js'
import ReadinessNotice from './ReadinessNotice.jsx'
import styles from './BowlCommittees.module.css'

/**
 * BOWL COMMITTEES — every bowl as a BUYER, off the Bowl Eligibility tab.
 *
 * Brian Kass, 2026-10-26: "create a build off the bowl eligibility tab that
 * shows every bowl, who is assigned and create assignments for pursuit as we
 * must reach out to bowl committees asap. we will include artwork and full
 * court press to impress these bowl committees so we can get official
 * provider opportunities." Ruling the same day: John Haggerty owns every bowl
 * committee; one desk leader may ride as the second, shown as a proposal an
 * exec can change.
 *
 * Phone first: one stacked card per bowl, the next action and the four
 * actions (Art, Email, Quote, Win) above the fold of the card, the rest one
 * tap behind it. Every gap carries the control that fixes it. Nothing here
 * sends an email: Email composes a DRAFT in the rep's own Gmail and stops.
 */

/** Teams | Committees, the view switch the Bowl Eligibility page renders. */
export function CommitteeSwitch({ view, onChange }) {
  return (
    <div className={styles.switch} role="group" aria-label="Bowl Eligibility view">
      {[['teams', 'Teams'], ['committees', 'Bowl committees']].map(([k, label]) => (
        <button key={k} type="button" aria-pressed={view === k}
          className={`${styles.chip} ${view === k ? styles.chipOn : ''}`} onClick={() => onChange(k)}>
          {label}
        </button>
      ))}
    </div>
  )
}

const err = (e, fallback) => e?.body?.detail || e?.body?.error || e?.message || fallback

export default function BowlCommittees() {
  const { user } = useAuth()
  /* Whether to offer the setup fix. UX only: the server decides the write. */
  const { ctx: accessCtx } = useAccess()
  const viewerIsExec = accessCtx?.role === 'exec'
  /* `?bowl=` is how a worklist item lands on its card: the whole list, that
   * card open. Without it the screen opens on the next eight weeks. */
  const [params] = useSearchParams()
  const focus = params.get('bowl') || ''
  const [state, setState] = useState({ status: 'loading', data: null, error: '' })
  const [filter, setFilter] = useState(focus ? 'all' : 'soon')

  const pendingRead = useRef(null)
  const load = useCallback(() => {
    pendingRead.current?.abort()
    const ctl = new AbortController()
    pendingRead.current = ctl
    setState((s) => ({ ...s, status: s.data ? 'refreshing' : 'loading' }))
    api.bowlCommittees({signal: ctl.signal})
      .then((d) => { if (!ctl.signal.aborted) setState({ status: 'ready', data: d, error: '' }) })
      .catch((e) => { if (!ctl.signal.aborted) setState({ status: 'error', data: null, error: err(e, 'The bowl committees could not be read.') }) })
  }, [setState])
  useEffect(() => { load(); return () => pendingRead.current?.abort() }, [load])

  const d = state.data
  /* Login id too (John = `haggerty`), so "Mine" survives a display-name edit. */
  const names = useMemo(() => [user?.name, user?.email, user?.id, user?.username].filter(Boolean), [user])
  const bowls = useMemo(() => applyFilter(d?.bowls || [], filter, names), [d, filter, names])
  const tiers = tierCounts(d?.bowls || [])
  const c = d?.counts || {}
  const daysToSelection = d?.selectionDay
    ? Math.max(0, Math.round((new Date(`${d.selectionDay}T12:00:00Z`) - Date.now()) / 86_400_000)) : null

  if (state.status === 'loading') return <p className={styles.muted}>Reading the bowl committees…</p>
  if (state.status === 'error') {
    return (
      <div className={styles.wrap}>
        <p className={styles.bad}>The bowl committees could not be read, so nothing is shown. This is not an empty list.</p>
        <p className={styles.muted}>{state.error}</p>
        <div className={styles.actions}><button type="button" className={styles.btn} onClick={load}>Try again</button></div>
      </div>
    )
  }

  return (
    <BowlMutationProvider meta={state.status === 'ready' ? {...d?.committeeMeta,season:d?.season} : null}>
    <div className={styles.wrap}>
      <section className={styles.summary} aria-label="Bowl committees summary">
        <p className={styles.big}>
          {c.bowls} bowls · proposed default owner: {d?.rule?.owner?.name}
        </p>
        <p className={styles.sub}>
          The committee is the buyer. Work it before selection day ({d?.selectionDay}
          {daysToSelection != null ? `, ${daysToSelection} days` : ''}); after that it already knows its matchup.
        </p>
        <ul className={styles.stats}>
          <li className={styles.stat}><span className={styles.statNum}>{tiers.P1}</span><span className={styles.statLabel}>kick off in the next 8 weeks</span></li>
          <li className={styles.stat}><span className={styles.statNum}>{c.withSourcedContact}</span><span className={styles.statLabel}>with a sourced direct contact</span></li>
          <li className={styles.stat}><span className={styles.statNum}>{c.secondBy?.Open ?? 0}</span><span className={styles.statLabel}>second seats open for an exec</span></li>
          <li className={styles.stat}><span className={styles.statNum}>{c.items?.open ?? 0}</span><span className={styles.statLabel}>open worklist items</span></li>
        </ul>
        <details>
          <summary>How the second person is picked</summary>
          <ul className={styles.rows}>{(d?.rule?.second || []).map((x) => <li key={x} className={styles.row} aria-disabled="true">{x}</li>)}</ul>
        </details>
      </section>

      <div className={styles.chips} role="group" aria-label="Filter bowls">
        {FILTERS.map((f) => (
          <button key={f.key} type="button" aria-pressed={filter === f.key}
            className={`${styles.chip} ${filter === f.key ? styles.chipOn : ''}`} onClick={() => setFilter(f.key)}>
            {f.label} · {applyFilter(d?.bowls || [], f.key, names).length}
          </button>
        ))}
      </div>

      {bowls.length === 0 && (
        <p className={styles.muted}>
          {filter === 'mine' ? 'No bowl lists you as owner or second. ' : 'Nothing matches this filter. '}
          The full list is under All bowls.
        </p>
      )}
      {/* Post-V4.001: a missing assignment roster is SETUP, not lost history.
          "Committee history is unavailable" stays for a real ledger read failure. */}
      <ReadinessNotice readiness={readinessOf(d?.committeeMeta)} isExec={viewerIsExec}
        classes={{ bad: styles.bad, setup: styles.setup, setupText: styles.setupText, btn: `${styles.btn} ${styles.setupBtn}` }}
        unavailableText="Committee history is unavailable. Changes are blocked until a current read succeeds." />
      <ul className={styles.list}>
        {bowls.map((b) => <BowlCard key={b.slug} bowl={b} canWork={state.status === 'ready' && b.canWork === true && d?.committeeMeta?.ready === true} canAssign={state.status === 'ready' && d?.canAssign === true && d?.committeeMeta?.ready === true} onChanged={load} focused={b.slug === focus} />)}
      </ul>
    </div>
    </BowlMutationProvider>
  )
}

function BowlCard({ bowl, canWork = false, canAssign, onChanged, focused = false }) {
  const [open, setOpen] = useState(focused)
  const ref = useCallback((el) => { if (el && focused) el.scrollIntoView({ block: 'start' }) }, [focused])
  const [email, setEmail] = useState(false)
  const s = bowl.assignment.second
  const href = recordHref(bowl.record?.championshipId)
  const quote = () => window.open(pricingDeepLink({
    championshipId: bowl.record?.championshipId, champion: { school: '' },
    championship: { sport: 'Football', level: 'Bowl' },
  }), '_blank', 'noopener,noreferrer')
  const blocked = Object.entries(bowl.ctas || {}).filter(([, v]) => v.kind === 'blocked')

  return (
    <li ref={ref} className={`${styles.card} ${bowl.priority.tier === 'P1' ? styles.cardP1 : ''}`}>
      <button type="button" className={styles.top} aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span className={styles.name}>{bowl.name}</span>
        <span className={styles.when}>
          {bowl.date || 'date not confirmed'} · {daysLabel(bowl.priority)}{' '}
          <span className={`${styles.tier} ${bowl.priority.tier === 'P1' ? styles.tierP1 : ''}`}>{bowl.priority.tier}</span>
        </span>
      </button>
      <p className={styles.who}>
        <span><b>{bowl.assignment.owner.source === 'proposal' ? 'Proposed owner:' : 'Owner:'}</b> {bowl.assignment.owner.name} <span className={styles.stamp}>({bowl.assignment.owner.source === 'proposal' ? 'proposal only; no access or assignment granted' : `set by ${bowl.assignment.owner.authorizedBy || 'an authorized person'}`})</span></span>
        <span><b>Second:</b> {secondLabel(s)} <span className={styles.stamp}>({secondSource(s)})</span></span>
        <span><b>Stage:</b> step {Math.min(bowl.stage.index, 6)} of 6 · {bowl.stage.label}</span>
      </p>
      {bowl.nextAction && (
        <p className={styles.next}>
          <span><b>Next:</b> {bowl.nextAction.action}</span>
          <span className={styles.why}>{bowl.nextAction.reason} Due {bowl.nextAction.due}.</span>
        </p>
      )}
      <div className={styles.ctas}>
        {href ? <Link className={styles.cta} to={href}>Art</Link>
          : <button type="button" className={`${styles.cta} ${styles.ctaOff}`} aria-disabled="true" onClick={() => setOpen(true)}>Art</button>}
        <button type="button" className={`${styles.cta} ${bowl.ctas.email.kind === 'blocked' ? styles.ctaOff : ''}`}
          aria-disabled={bowl.ctas.email.kind === 'blocked'} onClick={() => { setEmail((v) => !v); if (bowl.ctas.email.kind === 'blocked') setOpen(true) }}>
          Email
        </button>
        {href ? <button type="button" className={styles.cta} onClick={quote}>Quote</button>
          : <button type="button" className={`${styles.cta} ${styles.ctaOff}`} aria-disabled="true" onClick={() => setOpen(true)}>Quote</button>}
        {href ? <Link className={styles.cta} to={href}>Win</Link>
          : <button type="button" className={`${styles.cta} ${styles.ctaOff}`} aria-disabled="true" onClick={() => setOpen(true)}>Win</button>}
      </div>
      {blocked.length > 0 && (
        <p className={styles.blocked}>{blocked.map(([k, v]) => `${k[0].toUpperCase()}${k.slice(1)}: ${v.why}`).join(' ')}</p>
      )}
      {email && bowl.ctas.email.kind !== 'blocked' && <EmailDraft bowl={bowl} />}
      {open && <BowlDetail bowl={bowl} canWork={canWork} canAssign={canAssign} onChanged={onChanged} />}
    </li>
  )
}

export function EmailDraft({ bowl }) {
  const {user}=useAuth()
  const key=JSON.stringify([bowl.slug,bowl.record,user?.id,user?.name])
  const [selection,setSelection]=useState(''),[result,setResult]=useState(null)
  useEffect(()=>{
    let current=true
    request(`/api/bowl-committees/${encodeURIComponent(bowl.slug)}/introduction`,{query:selection?{jobId:selection}:{}})
      .then(data=>{if(current)setResult({key,data})})
      .catch(error=>{if(current)setResult({key,data:{available:false,state:'unavailable',missing:[error.message],subject:'',body:''}})})
    return()=>{current=false}
  },[key,selection,bowl.slug,setResult])
  const draft=result?.key===key?result.data:null
  const href=draft?.available?draft.composerHref:committeeComposerHref(bowl)
  const contact=draft?.available?draft.toPerson:bowl.draft.toPerson
  const recipients=draft?.available?draft.to:bowl.draft.to
  return (
    <div className={styles.section}>
      <h3 className={styles.h}>{draft?.available ? 'Proposed introduction copy, preview only' : 'Prepare the bowl introduction'}</h3>
      <p className={styles.muted}>Suggested contact: {contact.name}, {contact.title} · {recipients.join(', ')}</p>
      {!draft?<p role="status">Checking saved artwork and current bowl access.</p>:draft.available
        ? <><p className={styles.muted}>Verified original: {draft.attachmentName}. The composer will recheck this original before creating a draft.</p><p className={styles.draft}>{`Subject: ${draft.subject}\n\n${draft.body}`}</p></>
        : <p className={styles.muted}>{draft.missing?.join(' ')}</p>}
      {draft?.candidates?.length>1&&<label>Saved concept<select value={selection} onChange={e=>{setResult(null);setSelection(e.target.value)}}><option value="">Select a concept</option>{draft.candidates.map(row=><option key={row.jobId} value={row.jobId}>{row.title}</option>)}</select></label>}
      <div className={styles.actions}>
        {href ? <Link className={styles.btn} to={href}>{draft?.available ? 'Review this concept in the artwork composer' : 'Open bowl record to prepare artwork'}</Link>
          : <p className={styles.bad}>A matching record and account target are required before opening the composer. Confirm the bowl record first.</p>}
      </div>
      <p className={styles.muted}>No draft has been created. The existing record composer must confirm the current account, your record access, title and contact facts, sender and mailbox consent. A proposed committee seat grants no record or mailbox access. This preview is not automatically sent or copied into a draft.</p>
    </div>
  )
}

function BowlDetail({ bowl, canWork, canAssign, onChanged }) {
  return (
    <>
      <StepList bowl={bowl} canWork={canWork} onChanged={onChanged} />
      {bowl.gaps.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.h}>Needs a fix · {bowl.gaps.length}</h3>
          <ul className={styles.rows}>
            {bowl.gaps.map((g) => <GapFix key={g.key} bowl={bowl} gap={g} canWork={canWork} canAssign={canAssign} onChanged={onChanged} />)}
          </ul>
        </div>
      )}
      <div className={styles.section}>
        <h3 className={styles.h}>Committee</h3>
        <p className={styles.muted}>
          {bowl.operatingEntity?.name}{bowl.sponsoredTitle ? ` · ${bowl.sponsoredTitle}` : ''} · {bowl.venue}, {bowl.city} {bowl.state}
        </p>
        <ul className={styles.rows}>
          {bowl.decisionMakers.map((p) => (
            <li key={`${p.name}-${p.role}`} className={styles.row}>
              <span><b>{p.name}</b>, {p.title} <span className={styles.stamp}>({roleLabel(p.role)}{p.addedBy ? `, added by ${p.addedBy}` : ''})</span></span>
              {(p.email || p.phone) && <span>{[p.email, p.phone].filter(Boolean).join(' · ')}</span>}
              {!p.email && !p.phone && <span className={styles.stamp}>No published direct line.</span>}
              {p.sourceUrl && <a className={styles.link} href={p.sourceUrl} target="_blank" rel="noreferrer noopener">source</a>}
            </li>
          ))}
        </ul>
        {bowl.orgFallback?.phone && <p className={styles.muted}>Office: {bowl.orgFallback.phone} (an office line, not a decision-maker)</p>}
        <p className={styles.muted}>
          Current ring or awards provider: {bowl.incumbentProvider?.name || 'none publicly named'}
          {bowl.incumbentProvider?.name ? ` (${bowl.incumbentProvider.what || 'awards'}). An incumbent is the wedge, not a reason to skip.` : '.'}
        </p>
        <p className={styles.muted}>Tie-ins: {bowl.tieInBasis}</p>
        {bowl.dateSource?.url && <a className={styles.link} href={bowl.dateSource.url} target="_blank" rel="noreferrer noopener">date source</a>}
      </div>
      <div className={styles.section}>
        <h3 className={styles.h}>Teams likely tied to it · {bowl.linked.total}</h3>
        {bowl.linked.teams.length === 0
          ? <p className={styles.muted}>{bowl.linked.note || 'None on the board yet.'}</p>
          : (
            <ul className={styles.rows}>
              {bowl.linked.teams.map((t) => (
                <li key={t.name} className={styles.row}>
                  <Link className={styles.link} to="/bowl-eligibility">{t.name}</Link> · {t.conference} · {t.record} · {t.group}{t.via ? ` · ${t.via}` : ''}
                </li>
              ))}
            </ul>
          )}
        {bowl.linked.note && bowl.linked.teams.length > 0 && <p className={styles.muted}>{bowl.linked.note}</p>}
      </div>
      {canAssign && <SeatEditor bowl={bowl} onChanged={onChanged} />}
      {bowl.assignment.history.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.h}>Assignment history</h3>
          <ul className={styles.rows}>
            {bowl.assignment.history.map((h) => (
              <li key={h.id} className={styles.row} aria-disabled="true">
                {h.at?.slice(0, 10)} · {h.seat} · {h.action === 'clear' ? 'cleared' : h.person} · {h.actorName} · {h.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  )
}

function StepList({ bowl, canWork, onChanged }) {
  const mutate = useBowlMutation()
  const [editing, setEditing] = useState('')
  const [evidence, setEvidence] = useState('')
  const [st, setSt] = useState({ status: 'idle', msg: '' })
  const send = (key, action) => {
    if (!canWork) { setSt({status:'error',msg:'No current confirmed committee assignment grants this action.'}); return }
    setSt({ status: 'busy', msg: '' })
    mutate(body => api.bowlCommitteeStep(bowl.slug, body), { key, action, evidence })
      .then(() => { setSt({ status: 'idle', msg: '' }); setEditing(''); setEvidence(''); onChanged?.() })
      .catch((e) => setSt({ status: 'error', msg: err(e, 'That was not recorded.') }))
  }
  return (
    <div className={styles.section}>
      <h3 className={styles.h}>Full-court press</h3>
      {!canWork && <p className={styles.muted}>Read-only. A proposal is not an assignment; current confirmed committee access is required to record work.</p>}
      <ul className={styles.rows}>
        {bowl.steps.map((s) => (
          <li key={s.key} className={styles.row}>
            <span>
              <b>{s.index}. {s.label}</b>{' '}
              {s.status === 'done' ? <span className={styles.done}>done {s.done?.at?.slice(0, 10)} by {s.done?.by}</span>
                : <span className={styles.stamp}>{s.status} · due {s.due}</span>}
            </span>
            {s.status !== 'done' && <span>{s.action}</span>}
            {canWork && s.status !== 'done' && editing !== s.key && (
              <div className={styles.actions}>
                <button type="button" className={`${styles.btn} ${styles.btnQuiet}`} onClick={() => { setEditing(s.key); setEvidence('') }}>Mark done</button>
              </div>
            )}
            {canWork && s.status === 'done' && (
              <div className={styles.actions}>
                <button type="button" className={`${styles.btn} ${styles.btnQuiet}`} onClick={() => send(s.key, 'reopen')}>Reopen</button>
              </div>
            )}
            {editing === s.key && (
              <div className={styles.form}>
                <label className={styles.muted} htmlFor={`ev-${bowl.slug}-${s.key}`}>Evidence: {s.evidenceAsk}</label>
                <input id={`ev-${bowl.slug}-${s.key}`} className={styles.input} value={evidence} onChange={(e) => setEvidence(e.target.value)} />
                <div className={styles.actions}>
                  <button type="button" className={styles.btn} disabled={st.status === 'busy' || evidence.trim().length < 2} onClick={() => send(s.key, 'done')}>Save</button>
                  <button type="button" className={`${styles.btn} ${styles.btnQuiet}`} onClick={() => setEditing('')}>Cancel</button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
      {st.status === 'error' && <p className={styles.bad}>{st.msg}</p>}
    </div>
  )
}

function GapFix({ bowl, gap, canWork, canAssign, onChanged }) {
  const mutate = useBowlMutation()
  const [form, setForm] = useState({})
  const [show, setShow] = useState(false)
  const [st, setSt] = useState({ status: 'idle', msg: '' })
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))
  const fail = (e) => setSt({ status: 'error', msg: err(e, 'That was not recorded.') })
  const done = () => { setSt({ status: 'idle', msg: '' }); setShow(false); onChanged?.() }

  const saveContact = async () => {
    setSt({ status: 'busy', msg: '' })
    try {
      await mutate(body => api.bowlCommitteeStep(bowl.slug, body), { key: 'contact', evidence: form.sourceUrl,
        contact: { name: form.name, title: form.title, role: form.role || 'executive', email: form.email, phone: form.phone, sourceUrl: form.sourceUrl } })
      done()
    } catch (e) { fail(e) }
  }
  const saveDate = () => {
    setSt({ status: 'busy', msg: '' })
    mutate(body => api.bowlCommitteeStep(bowl.slug, body), { key: 'date', evidence: form.sourceUrl, date: { date: form.date, sourceUrl: form.sourceUrl } })
      .then(done).catch(fail)
  }
  const saveSecond = () => {
    setSt({ status: 'busy', msg: '' })
    mutate(body => api.bowlCommitteeAssign(bowl.slug, body), { seat: 'second', person: form.person, reason: form.reason })
      .then(done).catch(fail)
  }
  const saveRecord = () => {
    setSt({ status: 'busy', msg: '' })
    mutate(body => api.bowlCommitteeStep(bowl.slug, body), { key: 'record', evidence: form.recordId }).then(done).catch(fail)
  }
  const cands = bowl.assignment.second.candidates || []
  const canFix = gap.cta === 'pick-second' ? canAssign : canWork

  return (
    <li className={styles.row}>
      <span className={styles.gap}>{gap.label}</span>
      <span>{gap.action}</span>
      <span className={styles.why}>{gap.reason}</span>
      {!canFix && <span className={styles.stamp}>A current authorized person must record this change.</span>}
      {canFix && !show && (
        <div className={styles.actions}>
          <button type="button" className={styles.btn} onClick={() => setShow(true)}>Fix it</button>
        </div>
      )}
      {show && gap.cta === 'add-contact' && (
        <div className={styles.form}>
          {[['name', 'Name'], ['title', 'Title'], ['email', 'Email (the person\'s own)'], ['phone', 'Phone'], ['sourceUrl', 'Where it is published (URL)']].map(([k, label]) => (
            <input key={k} aria-label={label} placeholder={label} className={styles.input} value={form[k] || ''} onChange={set(k)} />
          ))}
          <select aria-label="Role" className={styles.input} value={form.role || 'executive'} onChange={set('role')}>
            {['executive', 'operations', 'team_services', 'partnerships'].map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
          </select>
          <div className={styles.actions}>
            <button type="button" className={styles.btn} disabled={st.status === 'busy' || !form.name || !(form.email || form.phone) || !form.sourceUrl} onClick={saveContact}>Save sourced contact evidence</button>
            <button type="button" className={`${styles.btn} ${styles.btnQuiet}`} onClick={() => setShow(false)}>Cancel</button>
          </div>
        </div>
      )}
      {show && gap.cta === 'confirm-date' && (
        <div className={styles.form}>
          <input type="date" aria-label="Game date" className={styles.input} value={form.date || ''} onChange={set('date')} />
          <input aria-label="Bowl page with the date (URL)" placeholder="Bowl page with the date (URL)" className={styles.input} value={form.sourceUrl || ''} onChange={set('sourceUrl')} />
          <div className={styles.actions}>
            <button type="button" className={styles.btn} disabled={st.status === 'busy' || !form.date || !form.sourceUrl} onClick={saveDate}>Save date</button>
            <button type="button" className={`${styles.btn} ${styles.btnQuiet}`} onClick={() => setShow(false)}>Cancel</button>
          </div>
        </div>
      )}
      {show && gap.cta === 'pick-second' && (
        <div className={styles.form}>
          <select aria-label="Second person" className={styles.input} value={form.person || ''} onChange={set('person')}>
            <option value="">Pick a desk leader</option>
            {(cands.length ? cands : PICKABLE).map((r) => <option key={r.short} value={r.short}>{r.pairedWith ? `${r.name} + ${r.pairedWith}` : r.name}</option>)}
          </select>
          <input aria-label="Reason" placeholder="Reason (required)" className={styles.input} value={form.reason || ''} onChange={set('reason')} />
          <div className={styles.actions}>
            <button type="button" className={styles.btn} disabled={st.status === 'busy' || !form.person || (form.reason || '').trim().length < 4} onClick={saveSecond}>Save</button>
            <button type="button" className={`${styles.btn} ${styles.btnQuiet}`} onClick={() => setShow(false)}>Cancel</button>
          </div>
        </div>
      )}
      {show && gap.cta === 'create-record' && (
        <div className={styles.form}>
          <input aria-label="Championship record id" placeholder="Championship record id once created" className={styles.input} value={form.recordId || ''} onChange={set('recordId')} />
          <div className={styles.actions}>
            <button type="button" className={styles.btn} disabled={st.status === 'busy' || !form.recordId} onClick={saveRecord}>Save</button>
            <button type="button" className={`${styles.btn} ${styles.btnQuiet}`} onClick={() => setShow(false)}>Cancel</button>
          </div>
        </div>
      )}
      {st.status === 'error' && <p className={styles.bad}>{st.msg}</p>}
    </li>
  )
}

/* For a bowl with no tie-in (the CFP games), the second can be any desk leader.
 * The server re-checks the name; this list only spares an exec the typing. */
const PICKABLE = [
  { short: 'Philip', name: 'Philip Davanzo III', pairedWith: 'Madison Gill' },
  { short: 'Zach', name: 'Zach Muller', pairedWith: 'Alexis Usery' },
  { short: 'Chris', name: 'Chris Powell', pairedWith: null },
  { short: 'Dalton', name: 'Dalton Demos', pairedWith: null },
  { short: 'Jonathan', name: 'Jonathan Harrison', pairedWith: null },
]

function SeatEditor({ bowl, onChanged }) {
  const mutate = useBowlMutation()
  const saving = useRef(false)
  const [form, setForm] = useState({ person: '', reason: '' })
  const [st, setSt] = useState({ status: 'idle', msg: '' })
  const s = bowl.assignment.second
  const cands = (s.candidates || []).length ? s.candidates : PICKABLE
  const save = (body) => {
    if (saving.current) return
    saving.current = true
    setSt({ status: 'busy', msg: '' })
    mutate(body => api.bowlCommitteeAssign(bowl.slug, body), body)
      .then(() => { setSt({ status: 'idle', msg: '' }); setForm({ person: '', reason: '' }); onChanged?.() })
      .catch((e) => setSt({ status: 'error', msg: err(e, 'That was not saved.') }))
      .finally(() => { saving.current = false })
  }
  return (
    <>
      <OwnerEditor bowl={bowl} onChanged={onChanged} />
    <div className={styles.section}>
      <h3 className={styles.h}>Change the second person</h3>
      <div className={styles.form}>
        <select aria-label="Second person" className={styles.input} value={form.person} onChange={(e) => setForm((f) => ({ ...f, person: e.target.value }))}>
          <option value="">Pick a desk leader</option>
          {cands.map((r) => <option key={r.short} value={r.short}>{r.pairedWith ? `${r.name} + ${r.pairedWith}` : r.name}</option>)}
        </select>
        <input aria-label="Reason" placeholder="Reason (required)" className={styles.input} value={form.reason}
          onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} />
        <div className={styles.actions}>
          <button type="button" className={styles.btn} disabled={st.status === 'busy' || !form.person || form.reason.trim().length < 4}
            onClick={() => save({ seat: 'second', person: form.person, reason: form.reason })}>Save</button>
          {s.source === 'exec' && (
            <button type="button" className={`${styles.btn} ${styles.btnQuiet}`} disabled={st.status === 'busy' || form.reason.trim().length < 4}
              onClick={() => save({ seat: 'second', action: 'clear', reason: form.reason })}>Back to the proposal</button>
          )}
        </div>
        {st.status === 'error' && <p className={styles.bad}>{st.msg}</p>}
      </div>
    </div>
    </>
  )
}

/* The reviewed server policy permits confirming the proposed John seat, not
 * arbitrary owner replacement. A current authorized human still decides.
 * Clearing restores a display-only proposal, not an effective seat. */
function OwnerEditor({ bowl, onChanged }) {
  const mutate = useBowlMutation()
  const saving = useRef(false)
  const [form, setForm] = useState({ person: '', reason: '' })
  const [st, setSt] = useState({ status: 'idle', msg: '' })
  const owner = bowl.assignment.owner
  const choices = ownerChoices().filter((c) => owner.source === 'proposal' || c.value !== owner.name)
  const save = (body) => {
    // React state is not synchronous between same-turn presses. Keep the
    // local busy state owned by the first request; the shared server-facing
    // fence still protects other panels and close/reopen/navigation.
    if (saving.current) return
    saving.current = true
    setSt({ status: 'busy', msg: '' })
    mutate(body => api.bowlCommitteeAssign(bowl.slug, body), body)
      .then(() => { setSt({ status: 'idle', msg: '' }); setForm({ person: '', reason: '' }); onChanged?.() })
      .catch((e) => setSt({ status: 'error', msg: err(e, 'That was not saved.') }))
      .finally(() => { saving.current = false })
  }
  const ready = st.status !== 'busy' && form.reason.trim().length >= 4
  return (
    <div className={styles.section}>
      <h3 className={styles.h}>Assigned to</h3>
      <p className={styles.muted}>{owner.source === 'proposal' ? 'Proposed, not assigned:' : 'Now:'} <b>{owner.name}</b></p>
      <div className={styles.form}>
        <select aria-label="Assigned to" className={styles.input} value={form.person} onChange={(e) => setForm((f) => ({ ...f, person: e.target.value }))}>
          <option value="">Pick who it is assigned to</option>
          {choices.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <input aria-label="Reason for the assignment change" placeholder="Reason (required)" className={styles.input} value={form.reason}
          onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} />
        <div className={styles.actions}>
          <button type="button" className={styles.btn} disabled={!ready || !form.person}
            onClick={() => save({ seat: 'owner', person: form.person, reason: form.reason })}>Save assignment</button>
          {owner.source === 'exec' && (
            <button type="button" className={`${styles.btn} ${styles.btnQuiet}`} disabled={!ready}
              onClick={() => save({ seat: 'owner', action: 'clear', reason: form.reason })}>Back to proposed John policy</button>
          )}
        </div>
        {st.status === 'error' && <p className={styles.bad}>{st.msg}</p>}
      </div>
    </div>
  )
}
